import { useTranslation } from 'react-i18next';
import './Conversation.css';
import { useConversation } from './useConversation';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import React, { useContext, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AppContext, appContext } from '../../../context/AppContext';
import {
	ConversationMessage,
	ConversationAttachment,
	formatCurrency,
	formatFileSize,
} from '@anticatrade/shared';
import { Spinner, useDeferredLoading } from '@anticatrade/shared';
import { Heading } from '../../base/Heading/Heading';

const Conversation = () => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const messagesEndRef = useRef<HTMLDivElement>(null);

	const { api, merchant } = useContext(appContext as React.Context<AppContext>);
	const { isLoading, conversation } = useConversation();

	const { showSpinner, showContent } = useDeferredLoading(isLoading);

	// Scroll to bottom when messages load
	useEffect(() => {
		if (!isLoading && messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [isLoading, conversation?.messages]);

	const onBack = () => {
		navigate('/conversations');
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		const today = new Date();
		const yesterday = new Date(today);
		yesterday.setDate(yesterday.getDate() - 1);

		if (date.toDateString() === today.toDateString()) {
			return t('Today');
		} else if (date.toDateString() === yesterday.toDateString()) {
			return t('Yesterday');
		} else {
			return date.toLocaleDateString(i18n.language, {
				day: 'numeric',
				month: 'long',
				year: 'numeric',
			});
		}
	};

	const formatTime = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleTimeString(i18n.language, {
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const getDateKey = (dateString: string) => {
		return new Date(dateString).toDateString();
	};

	const renderAttachment = (att: ConversationAttachment) => {
		const attachmentURL = api.conversationAttachmentURL(att.id);
		const isImage = att.contentType.startsWith('image/');

		return (
			<a
				key={att.id}
				href={attachmentURL}
				target='_blank'
				rel='noopener noreferrer'
				className='conversation__attachment'
			>
				{isImage ? (
					<img
						src={attachmentURL}
						alt={att.fileName}
						loading='lazy'
						className='conversation__attachment-preview'
					/>
				) : (
					<SlIcon name='file-earmark' className='conversation__attachment-icon' />
				)}
				<span className='conversation__attachment-name'>{att.fileName}</span>
				<span className='conversation__attachment-size'>
					{formatFileSize(att.sizeBytes)}
				</span>
			</a>
		);
	};

	const renderMessages = (messages: ConversationMessage[]) => {
		const elements: React.ReactNode[] = [];
		let lastDateKey = '';

		messages.forEach((msg) => {
			const dateKey = getDateKey(msg.createdAt);

			// Add date separator if new day
			if (dateKey !== lastDateKey) {
				elements.push(
					<div key={`date-${dateKey}`} className='conversation__date-separator'>
						{formatDate(msg.createdAt)}
					</div>,
				);
				lastDateKey = dateKey;
			}

			const senderClass =
				msg.sender === 'buyer'
					? 'conversation__message--buyer'
					: msg.sender === 'merchant'
						? 'conversation__message--merchant'
						: 'conversation__message--system';

			elements.push(
				<div key={msg.id} className={`conversation__message ${senderClass}`}>
					<div className='conversation__message-content'>{msg.bodyText}</div>
					{msg.attachments && msg.attachments.length > 0 && (
						<div className='conversation__message-attachments'>
							{msg.attachments.map(renderAttachment)}
						</div>
					)}
					<div className='conversation__message-meta'>
						<span>{formatTime(msg.createdAt)}</span>
					</div>
				</div>,
			);
		});

		return elements;
	};

	if (!showContent) {
		return <div className='conversation'>{showSpinner && <Spinner />}</div>;
	}

	if (!conversation) {
		return (
			<div className='conversation'>
				<div className='conversation__empty-messages'>{t('Conversation not found')}</div>
			</div>
		);
	}

	return (
		<div className='conversation wrapped'>
			<div className='conversation__header'>
				<Heading
					left={
						<div className='conversation__header-name'>
							<SlButton
								className='conversation__back-button'
								variant='text'
								onClick={onBack}
							>
								<SlIcon name='chevron-left' slot='prefix' />
								{t('Back')}
							</SlButton>
							<div className='conversation__buyer-name'>{conversation.buyerName}</div>
						</div>
					}
					right={
						conversation.productID != null && (
							<div className='conversation__product'>
								<span className='conversation__product-label'>
									{t('Related product')}
								</span>
								<Link
									to={`/products/${conversation.productID}`}
									className='conversation__product-link'
								>
									{conversation.productImageFilename && (
										<img
											src={`${import.meta.env.VITE_PRODUCT_IMAGE_URL}/${conversation.productID}/thumbnail-${conversation.productImageFilename}.webp`}
											alt={conversation.productTitle}
											className='conversation__product-image'
										/>
									)}
									<div className='conversation__product-text'>
										<span className='conversation__product-title'>
											{conversation.productTitle}
										</span>
										{conversation.productPrice != null && (
											<div className='conversation__product-price'>
												{formatCurrency(
													conversation.productPrice,
													i18n.language,
													'EUR',
												)}
											</div>
										)}
									</div>
								</Link>
							</div>
						)
					}
				></Heading>
			</div>
			<div className='conversation__messages'>
				{conversation.messages?.length === 0 ? (
					<div className='conversation__empty-messages'>
						{t('No messages in this conversation')}
					</div>
				) : (
					<>
						{renderMessages(conversation.messages)}
						<p className='conversation__messages-sent-to-email-text'>
							{t('Message sent to email text').replace('{email}', merchant!.email!)}
						</p>
						<div ref={messagesEndRef} />
					</>
				)}
			</div>
		</div>
	);
};

export { Conversation };
